import React, { Ref, useMemo } from 'react'
import { StyleSheet } from 'react-native'
import { object, string } from 'yup'
import { Trans, useTranslation } from 'react-i18next'
import { Color, Spacing, Text } from 'zens-storybook'
import { CompanyData, CompanyWorkHours, DataWithUpload, Draft, useCompany } from 'zens-store'
import { observer } from 'mobx-react'
import { Form, NavigationConfirm, FormProps, CompanyWorkingHours } from '@dms/component'

import { ScheduleSliderRef } from '../schedule/slider'

const styles = StyleSheet.create({
  schedule: {
    maxWidth: '100%',
  },
  intro: {
    color: Color.metalGrey,
    marginBottom: Spacing.M,
  },
  skeletonIntro: {},
})

interface Data extends DataWithUpload<CompanyData> {
  location_enabled: boolean
}

interface Props extends Omit<FormProps, 'draft' | 'config' | 'schema' | 'buttons'> {
  companyId: number
  onSave?: (draft: Draft<DataWithUpload<CompanyData>>) => Promise<void>
  onDelete?: (draft: Draft<CompanyData>) => Promise<void>
  progress?: number
  test_sliderRef?: Ref<ScheduleSliderRef>
}

const CompanyForm = (props: Props) => {
  const { companyId, onSave, onDelete, test_sliderRef, ...otherProps } = props
  const company = useCompany({ companyId })
  const draft = useMemo(
    () => new Draft<Data>({ id: NaN, location_enabled: true, ...company.data?.data }),
    [company.data],
  )
  const { t } = useTranslation()

  const config = {
    groups: [
      {
        fields: [
          {
            key: 'name',
            label: t('Company name'),
            fieldProps: {
              testID: 'CompanyForm.Name',
            },
          },
        ],
      },
      {
        fields: [
          {
            key: 'workHours',
            label: t('Working hours'),
            FieldInputComponent: CompanyWorkingHours,
            inputStyle: styles.schedule,
            fieldProps: {
              companyId,
              value: { startAt: draft.data.workhours_starttime, endAt: draft.data.workhours_endtime },
              onChange: (value: CompanyWorkHours) => {
                draft.update('workhours_starttime', value.startAt)
                draft.update('workhours_endtime', value.endAt)
              },
              ref: test_sliderRef,
            },
          },
        ],
      },
    ],
  }
  const schema = object({
    name: string().required(t('Company name is required')),
  })
  const buttons = [
    [
      {
        title: t('Save'),
        onPress: onSave,
        validate: true,
      },
      {
        title: t('Cancel'),
        href: `/companies/${companyId}/detail`,
        light: true,
      },
    ],
    ...(onDelete
      ? [
          {
            title: t('Delete company'),
            onPress: onDelete,
            left: 'trash' as const,
            danger: true,
          },
        ]
      : []),
  ]
  return (
    <>
      <Text style={styles.intro}>
        <Trans>These preferences apply to all users on this account.</Trans>
      </Text>
      <Form draft={draft} config={config} schema={schema} buttons={buttons} {...otherProps} />
      <NavigationConfirm draft={draft} />
    </>
  )
}

const Skeleton = () => {
  const { t } = useTranslation()
  const config = {
    groups: [
      {
        fields: [
          {
            key: 'name',
            label: t('Company name'),
          },
        ],
      },
      {
        fields: [
          {
            key: 'workHours',
            label: t('Working hours'),
            inputStyle: styles.schedule,
            FieldInputComponent: CompanyWorkingHours.Skeleton,
          },
        ],
      },
    ],
  }
  return (
    <>
      <Text.Skeleton style={[styles.intro, styles.skeletonIntro]} />
      <Form.Skeleton config={config} numberOfbuttons={[2, 1]} />
    </>
  )
}
CompanyForm.Skeleton = Skeleton

export default observer(CompanyForm)
